import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlaidContext } from "../context/PlaidContext";
import { plaidApi } from "../services/plaidApi";
import TransactionsTable from "./TransactionsTable";
import Loader from "./Loader";
import { ROUTE_PATHS } from "../routes/paths";

const Dashboard = () => {
  const { accessToken, dispatch } = useContext(PlaidContext);
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState("dashboard");
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [recentTransactionCount, setRecentTransactionCount] = useState(0);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    dispatch({ type: "LOGOUT" });
    navigate(ROUTE_PATHS.CONNECT, { replace: true });
  };

  const handleConnectBank = () => {
    navigate(ROUTE_PATHS.CONNECT);
  };

  useEffect(() => {
    if (activeNav !== "dashboard" || !accessToken) {
      return;
    }

    const fetchDashboardData = async () => {
      try {
        setLoadingDashboard(true);
        setDashboardError(null);

        const [balanceResponse, transactionResponse] = await Promise.all([
          plaidApi.getBalance(accessToken),
          plaidApi.getTransactions(accessToken),
        ]);

        const accounts = balanceResponse?.accounts || [];
        const computedTotalBalance = accounts.reduce(
          (sum: number, account: any) => sum + (account?.balances?.current || 0),
          0,
        );

        const latestTransactions = transactionResponse?.latest_transactions || [];

        setTotalBalance(computedTotalBalance);
        setRecentTransactionCount(latestTransactions.length);
        setTransactions(latestTransactions);
      } catch (error) {
        setDashboardError("Unable to load realtime dashboard data.");
      } finally {
        setLoadingDashboard(false);
      }
    };

    fetchDashboardData();
  }, [activeNav, accessToken]);

  useEffect(() => {
    if (activeNav !== "transactions" || !accessToken) {
      return;
    }

    const fetchTransactions = async () => {
      try {
        setLoadingTransactions(true);
        setTransactionsError(null);
        const response = await plaidApi.getTransactions(accessToken);
        setTransactions(response.latest_transactions || []);
      } catch (error) {
        setTransactionsError("Unable to load transactions right now.");
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [activeNav, accessToken]);

  const pageTitle = useMemo(() => {
    if (activeNav === "transactions") return "Transactions";
    if (activeNav === "account") return "Account Details";
    if (activeNav === "settings") return "Settings";
    return "Dashboard";
  }, [activeNav]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-64 bg-white shadow-lg flex flex-col">
        {/* Logo */}
        <div className="px-6 py-5 border-b">
          <h1 className="text-2xl font-bold text-gray-900">Plaid</h1>
          {/* <p className="text-sm text-gray-500 mt-1">Dashboard</p> */}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {/* Dashboard */}
          <button
            onClick={() => setActiveNav("dashboard")}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeNav === "dashboard"
                ? "bg-gradient-to-r from-blue-600 to-purple-700 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2h2v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            <span className="font-medium">Dashboard</span>
          </button>

          {/* Transactions */}
          <button
            onClick={() => setActiveNav("transactions")}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeNav === "transactions"
                  ? "bg-gradient-to-r from-blue-600 to-purple-700 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <span className="font-medium">Transactions</span>
          </button>

          {/* Account Details */}
          {/* <button
            onClick={() => setActiveNav("account")}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeNav === "account"
                ? "bg-gray-100 text-gray-900"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Account Details</span>
          </button> */}

          {/* Settings */}
          {/* <button
            onClick={() => setActiveNav("settings")}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
              activeNav === "settings"
                ? "bg-gray-100 text-gray-900"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.06 2.042-.948 2.286-1.56.38-1.56 2.6 0 2.98a1.533 1.533 0 01.948 2.286c-.836 1.372.734 2.942 2.106 2.106a1.533 1.533 0 012.286.948c.38 1.56 2.6 1.56 2.98 0a1.533 1.533 0 012.286-.948c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.948-2.286c1.56-.38 1.56-2.6 0-2.98a1.532 1.532 0 01-.948-2.286c.836-1.372-.734-2.942-2.106-2.106a1.533 1.533 0 01-2.286-.948zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Settings</span>
          </button> */}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, User</span>
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              U
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {activeNav === "transactions" ? (
            <TransactionsTable
              transactions={transactions}
              loading={loadingTransactions}
              error={transactionsError}
            />
          ) : null}

          {activeNav === "account" ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Account Details</h2>
              <p className="text-gray-600">Account details screen can be extended here.</p>
            </div>
          ) : null}

          {activeNav === "settings" ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Settings</h2>
              <p className="text-gray-600">Settings screen can be extended here.</p>
            </div>
          ) : null}

          {activeNav === "dashboard" ? (
          <>
          {/* Cards Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            {/* Total Balance Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Balance</h3>
              {loadingDashboard ? (
                <div className="py-2">
                  <Loader text="Syncing" size="sm" />
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900 mb-2">${totalBalance.toFixed(2)}</p>
              )}
              <p className="text-xs text-gray-500">Connected account</p>
            </div>

            {/* Recent Transactions Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Recent Transactions</h3>
              {loadingDashboard ? (
                <div className="py-2">
                  <Loader text="Syncing" size="sm" />
                </div>
              ) : (
                <p className="text-3xl font-bold text-gray-900 mb-2">{recentTransactionCount}</p>
              )}
              <p className="text-xs text-gray-500">Last 30 days</p>
            </div>

            {/* Account Status Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Account Status</h3>
              {accessToken ? (
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <p className="text-lg font-semibold text-green-600">Connected</p>
                </div>
              ) : (
                <button
                  onClick={handleConnectBank}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg transition-all mb-2"
                >
                  Connect Bank
                </button>
              )}
              <p className="text-xs text-gray-500">
                {accessToken ? "Your bank account is connected" : "Click to create link token"}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Quick Stats</h2>
            {dashboardError ? (
              <p className="text-red-600">{dashboardError}</p>
            ) : (
              <p className="text-gray-600">
                Live data synced from Plaid. Open the Transactions tab to view full records.
              </p>
            )}
          </div>
          </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
